import React from 'react';
import { Card } from '../../common/Card';
import { StatusBadge, Typography } from '../../common';
import { homeElevatedInset } from '../../home/homeDashboardStyles';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';
import { formatLeagueDate, leaguePlayerNameClass } from '../../leagues/LeagueMatchViews';
import { ClubSectionIcon } from './ClubSectionIcons';

const leagueStripClass = `flex items-stretch gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;
const leagueCardClass = `flex shrink-0 flex-col gap-3 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start border-l-[3px] ${homeElevatedInset}`;

const RESULT_BADGE = {
  won: { variant: 'success', label: 'Won', accent: 'border-l-success' },
  lost: { variant: 'danger', label: 'Lost', accent: 'border-l-danger' },
  drawn: { variant: 'default', label: 'Drawn', accent: 'border-l-border-medium' },
};

const COMPETITION_TONE = {
  ladies: { chip: 'bg-female-light text-female', accent: 'border-l-female' },
  mixed: { chip: 'bg-team1-light text-team1', accent: 'border-l-team1' },
  open: { chip: 'bg-secondary/10 text-secondary', accent: 'border-l-secondary' },
  hybrid: { chip: 'bg-warning-light text-warning-dark', accent: 'border-l-warning' },
};

const competitionTone = (code) => COMPETITION_TONE[code] ?? COMPETITION_TONE.open;

const ClubStripDivider = () => (
  <div
    className="shrink-0 snap-start flex flex-col items-center self-stretch px-1 w-11 sm:w-12"
    role="separator"
    aria-label="Club teams"
  >
    <div className="flex flex-col items-center gap-1.5 h-full py-0.5">
      <div className="w-px flex-1 min-h-[1.25rem] bg-border-medium" />
      <span className="text-[9px] font-bold uppercase tracking-widest text-text-muted [writing-mode:vertical-rl] rotate-180 select-none">
        Club
      </span>
      <div className="w-px flex-1 min-h-[1.25rem] bg-border-medium" />
    </div>
  </div>
);

const SeasonSummaryCard = ({ summary }) => {
  const played = summary.matches.length;
  return (
    <Card variant="default" size="medium" className={`${leagueCardClass} border-l-primary`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary" aria-hidden>
          <ClubSectionIcon type="league" className="h-4 w-4" />
        </span>
        <Typography variant="caption" className="min-w-0 truncate text-[0.65rem] font-semibold uppercase tracking-wide text-primary">
          Your league season
        </Typography>
      </div>
      <div className="min-w-0">
        <Typography className={`text-sm font-semibold leading-snug ${leaguePlayerNameClass(summary.player)}`}>
          {summary.player?.display_name ?? 'League player'}
        </Typography>
        <Typography variant="caption" className="line-clamp-2 leading-snug text-text-secondary">
          {summary.teamNames.length ? summary.teamNames.join(' · ') : 'No appearances yet'}
        </Typography>
      </div>
      <dl className="mt-auto grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-surface-subtle px-2 py-2">
          <dt className="text-[0.65rem] uppercase tracking-wide text-text-muted">Played</dt>
          <dd className="text-xl font-bold tabular-nums text-text-primary">{played}</dd>
        </div>
        <div className="rounded-xl bg-success-light px-2 py-2">
          <dt className="text-[0.65rem] uppercase tracking-wide text-success-dark">Won</dt>
          <dd className="text-xl font-bold tabular-nums text-success">{summary.won}</dd>
        </div>
        <div className="rounded-xl bg-danger-light px-2 py-2">
          <dt className="text-[0.65rem] uppercase tracking-wide text-danger-dark">Lost</dt>
          <dd className="text-xl font-bold tabular-nums text-danger">{summary.lost}</dd>
        </div>
      </dl>
    </Card>
  );
};

const LeagueMatchCard = ({ match }) => {
  const badge = match.result ? RESULT_BADGE[match.result] : null;
  const accent = badge?.accent ?? 'border-l-primary';
  return (
    <Card variant="default" size="medium" className={`${leagueCardClass} ${accent}`}>
      <div className="flex items-center justify-between gap-2">
        <Typography variant="caption" className="text-[0.65rem] font-semibold uppercase tracking-wide text-primary">
          You played
        </Typography>
        <span className="shrink-0 rounded-full bg-surface-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          {match.isHome ? 'Home' : 'Away'}
        </span>
      </div>
      <div className="min-w-0">
        <Typography className="line-clamp-2 text-sm font-semibold leading-snug text-text-primary">
          {match.isHome ? 'vs' : 'at'} {match.opponentName}
        </Typography>
        <Typography variant="caption" className="leading-snug text-text-secondary">
          {match.teamName} · {formatLeagueDate(match.fixture.match_date)}
        </Typography>
      </div>
      <div className="mt-auto flex items-end justify-between gap-2">
        <span className="flex items-baseline gap-1.5 tabular-nums leading-none">
          <span className="text-3xl font-bold text-text-primary">{match.us ?? '–'}</span>
          <span className="text-lg font-medium text-text-muted">–</span>
          <span className="text-3xl font-bold text-text-primary">{match.them ?? '–'}</span>
        </span>
        {badge ? (
          <StatusBadge variant={badge.variant} size="small">{badge.label}</StatusBadge>
        ) : (
          <StatusBadge variant="default" size="small">Upcoming</StatusBadge>
        )}
      </div>
    </Card>
  );
};

const ordinal = (n) => {
  if (n == null) return '–';
  const remainder = n % 100;
  if (remainder >= 11 && remainder <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
};

const TeamStandingCard = ({ team }) => {
  const tone = competitionTone(team.leagueCode);
  const rank = ordinal(team.position);
  const record = team.won == null && team.lost == null ? '–' : `${team.won ?? 0}–${team.lost ?? 0}`;

  return (
    <Card variant="default" size="medium" className={`${leagueCardClass} ${tone.accent}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone.chip}`}>
            {team.leagueName || 'Club team'}
          </span>
          <Typography className="mt-2 line-clamp-2 text-sm font-semibold leading-snug text-text-primary">
            {team.name}
          </Typography>
          <Typography variant="caption" className="leading-snug text-text-secondary">
            {team.divisionName || 'County league'}
          </Typography>
        </div>
        <div className={`flex min-w-[4.5rem] shrink-0 flex-col items-center justify-center rounded-2xl px-3 py-2 ${tone.chip}`}>
          <span className="text-[0.6rem] font-semibold uppercase tracking-wider opacity-80">Pos</span>
          <span className="text-2xl font-bold tabular-nums leading-none">
            {rank}
          </span>
        </div>
      </div>
      <dl className="mt-auto grid grid-cols-2 gap-2 text-center">
        <div className="rounded-xl bg-surface-subtle px-2 py-2">
          <dt className="text-[0.65rem] uppercase tracking-wide text-text-muted">W–L</dt>
          <dd className="text-base font-bold tabular-nums text-text-primary">{record}</dd>
        </div>
        <div className="rounded-xl bg-surface-subtle px-2 py-2">
          <dt className="text-[0.65rem] uppercase tracking-wide text-text-muted">Pts</dt>
          <dd className="text-base font-bold tabular-nums text-text-primary">{team.points ?? '–'}</dd>
        </div>
      </dl>
    </Card>
  );
};

/**
 * League carousel for the My Club overview.
 * Linked members see their matches for this club, then a Club divider, then
 * compact club-team standings. Other members in a linked club see the team cards only.
 * Cards are not individually interactive; taps bubble to the parent section.
 */
export function ClubLeagueOverviewCarousel({ summary, teams = [] }) {
  const recent = summary ? summary.matches.slice(0, 5) : [];
  const hasPersonal = Boolean(summary);
  const hasTeams = teams.length > 0;
  if (!hasPersonal && !hasTeams) return null;

  const showDivider = hasPersonal && hasTeams;

  const swipeHint = hasPersonal && recent.length > 0
    ? 'Swipe for your matches and club teams · Tap to open League'
    : hasTeams && teams.length > 1
      ? 'Swipe for club teams · Tap to open League'
      : 'Tap to open League';

  return (
    <>
      <div
        role="region"
        aria-label={hasPersonal ? 'Your league matches' : 'Club league teams'}
        className={leagueStripClass}
      >
        {hasPersonal ? <SeasonSummaryCard summary={summary} /> : null}
        {recent.map((match) => (
          <LeagueMatchCard key={match.fixture.id} match={match} />
        ))}
        {showDivider ? <ClubStripDivider /> : null}
        {teams.map((team) => (
          <TeamStandingCard key={team.id} team={team} />
        ))}
      </div>
      <Typography variant="caption" className="mt-3 px-2 text-center text-text-muted sm:hidden">
        {swipeHint}
      </Typography>
    </>
  );
}
